import React, { useState, useRef, useEffect } from "react";

const TEAL  = "#0D7377";
const AMBER = "#F0A500";

/**
 * ChipInput — free text input with comma-aware autocomplete and chip display.
 *
 * Each comma-separated segment gets its own chip. Chips are validated against
 * the `options` array:
 *   - Exact match (case-insensitive) → green chip
 *   - No match → amber chip with warning
 *   - "All" → always green (special value)
 *
 * Props:
 *   value       {string}   Current value e.g. "Football, Cricket"
 *   onChange    {fn}       Called with new comma-joined string on every change
 *   options     {string[]} Valid autocomplete suggestions
 *   placeholder {string}
 *   label       {string}   Shown above input
 */
export default function ChipInput({ value, onChange, options = [], placeholder, label }) {
  // Parse existing value into chips on mount / external change
  const [chips,       setChips]       = useState(() => parseChips(value));
  const [inputVal,    setInputVal]    = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeSug,   setActiveSug]   = useState(-1);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  // Sync chips if parent value changes externally (e.g. form reset)
  useEffect(() => {
    const external = parseChips(value);
    if (JSON.stringify(external) !== JSON.stringify(chips)) {
      setChips(external);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function parseChips(str) {
    if (!str) return [];
    return str.split(",").map(s => s.trim()).filter(Boolean);
  }

  function emitChange(newChips) {
    onChange(newChips.join(", "));
  }

  function isValid(chip) {
    if (!chip) return false;
    if (chip.toLowerCase() === "all") return true;
    return options.some(o => o.toLowerCase() === chip.toLowerCase());
  }

  // Filter suggestions based on current input segment
  function getSuggestions(text) {
    if (!text.trim()) return [];
    const lower = text.trim().toLowerCase();
    // Always include "All" if it matches
    const filtered = [
      ...(("all").startsWith(lower) ? ["All"] : []),
      ...options.filter(o =>
        o.toLowerCase() !== "all" &&
        o.toLowerCase().includes(lower) &&
        !chips.some(c => c.toLowerCase() === o.toLowerCase())
      ),
    ].slice(0, 8);
    return filtered;
  }

  function handleInput(e) {
    const raw = e.target.value;

    // If user typed a comma, commit the current segment as a chip
    if (raw.endsWith(",")) {
      const segment = raw.slice(0, -1).trim();
      if (segment) commitChip(segment);
      setInputVal("");
      setSuggestions([]);
      setActiveSug(-1);
      return;
    }

    setInputVal(raw);
    setSuggestions(getSuggestions(raw));
    setActiveSug(-1);
  }

  function commitChip(text) {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Normalise to exact case from options if possible
    const match = options.find(o => o.toLowerCase() === trimmed.toLowerCase());
    const chip  = match || (trimmed.toLowerCase() === "all" ? "All" : trimmed);
    const updated = [...chips, chip];
    setChips(updated);
    emitChange(updated);
  }

  function handleKeyDown(e) {
    if (e.key === "ArrowDown") {
      setActiveSug(i => Math.min(i + 1, suggestions.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveSug(i => Math.max(i - 1, -1));
      e.preventDefault();
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (activeSug >= 0 && suggestions[activeSug]) {
        selectSuggestion(suggestions[activeSug]);
      } else if (inputVal.trim()) {
        commitChip(inputVal);
        setInputVal("");
        setSuggestions([]);
        setActiveSug(-1);
      }
      if (e.key === "Enter") e.preventDefault();
    } else if (e.key === "Backspace" && !inputVal) {
      // Remove last chip on backspace when input is empty
      const updated = chips.slice(0, -1);
      setChips(updated);
      emitChange(updated);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setActiveSug(-1);
    }
  }

  function selectSuggestion(opt) {
    const updated = [...chips, opt];
    setChips(updated);
    emitChange(updated);
    setInputVal("");
    setSuggestions([]);
    setActiveSug(-1);
    inputRef.current?.focus();
  }

  function removeChip(index) {
    const updated = chips.filter((_, i) => i !== index);
    setChips(updated);
    emitChange(updated);
    inputRef.current?.focus();
  }

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (listRef.current && !listRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Commit inputVal when input loses focus
  function handleBlur() {
    setTimeout(() => {
      if (inputVal.trim()) {
        commitChip(inputVal);
        setInputVal("");
        setSuggestions([]);
      }
    }, 150); // small delay so suggestion click fires first
  }

  const valid   = isValid;
  const invalid = chip => !isValid(chip);

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      )}
      <div
        className="min-h-[38px] w-full border border-gray-300 rounded-lg px-2 py-1.5 flex flex-wrap gap-1 items-center cursor-text focus-within:ring-2 focus-within:ring-teal-500 bg-white"
        onClick={() => inputRef.current?.focus()}
      >
        {/* Chips */}
        {chips.map((chip, i) => (
          <span
            key={i}
            className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
            style={
              valid(chip)
                ? { backgroundColor: "#E6F4F1", color: TEAL }
                : { backgroundColor: "#FEF3C7", color: "#92400E" }
            }
            title={invalid(chip) ? `"${chip}" not found in mapping table` : undefined}
          >
            {invalid(chip) && <span title="Not in mapping table">⚠ </span>}
            {chip}
            <button
              onClick={e => { e.stopPropagation(); removeChip(i); }}
              className="ml-0.5 hover:opacity-70 font-bold leading-none"
            >×</button>
          </span>
        ))}

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="flex-1 min-w-24 text-sm outline-none bg-transparent py-0.5"
          placeholder={chips.length === 0 ? (placeholder || "Type to search, comma to add multiple...") : ""}
        />
      </div>

      {/* Suggestions dropdown */}
      {suggestions.length > 0 && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto"
          style={{ minWidth: "200px" }}
        >
          {suggestions.map((opt, i) => (
            <button
              key={opt}
              onMouseDown={e => { e.preventDefault(); selectSuggestion(opt); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
              style={{
                backgroundColor: i === activeSug ? "#E6F4F1" : undefined,
                color: i === activeSug ? TEAL : "#374151",
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {/* Validation warning */}
      {chips.some(c => !isValid(c)) && (
        <p className="text-xs mt-1" style={{ color: AMBER }}>
          ⚠ Amber chips don't match mapping table values — double-check spelling.
        </p>
      )}
    </div>
  );
}